import React, {useState, useMemo} from "react";
import {motion, AnimatePresence} from "framer-motion";
import {Box, Typography, Stack, Skeleton, TextField, InputAdornment} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {EngineOnIcon} from "../../../utils/ui/Icons/EngineOnIcon.js";
import {EngineOffIcon} from "../../../utils/ui/Icons/EngineOff.js";
import "./LaveuseListPanel.css"
import {NoUserIcon} from "../../../utils/ui/Icons/NoUserIcon.js";

const engineStateLabel = {
    on: "En route",
    off: "Arrêté",
    idle: "En pause"
};

const engineStateColor = {
    on: "#5cb85c",
    lavage: "#5cb85c",
    off: "#a0aec0",
    idle: "#f6c94e"
};

const getCardStyle = (engine, speed, hasFaults) => {
    let backgroundColor = hasFaults ? '#fff5f5' : '#f4f6f9';
    let borderColor = hasFaults ? '#fca5a5' : 'transparent';
    let backgroundImage = undefined;
    if (!hasFaults) {
        if (engine === 'lavage') { backgroundColor = '#e4f8b4'; borderColor = '#7bb421'; }
        else if (engine === 'on' && speed > 0) { backgroundColor = '#F0F7E8'; borderColor = '#C0DD97'; }
        else if (engine === 'idle' || (engine === 'on' && !(speed > 0))) { backgroundColor = '#FEFAF2'; borderColor = '#FAC775'; }
        else if (engine === 'off') {
            backgroundColor = '#f4f6f9';
            backgroundImage = 'repeating-linear-gradient(-45deg, transparent, transparent 6px, rgba(0,0,0,0.03) 6px, rgba(0,0,0,0.03) 8px)';
        }
    }
    return { backgroundColor, borderColor, backgroundImage };
};

export default function LaveuseListPanel({laveuses, telematics, onSelect, selectedId}) {
    const [search, setSearch] = useState("");

    const sorted = useMemo(() => {
        return Array.isArray(laveuses) ? laveuses : [];
    }, [laveuses]);

    const getTelemetricValue = (lav, field) => {
        return telematics?.[lav.id.toString()]?.live?.[field] ?? lav?.live?.[field] ?? null;
    };

    const hasTelematicsLoaded = (lavId) => {
        return telematics?.[lavId]?.live != null;
    };

    const getEngineState = (lav) => {
        return telematics?.[lav.id]?.live?.engineState ?? lav?.live?.engineState ?? null;
    };

    const getEngineIcon = (state) => {
        if (state === 'on') return <EngineOnIcon color="#5cb85c" fontSize={16}/>;
        if (state === 'idle') return <EngineOnIcon color="#f6c94e" fontSize={16}/>;
        if (state === 'off') return <EngineOffIcon color="#a0aec0" fontSize={16}/>;
        return null;
    };

    const formatOdometer = (meters) => {
        if (meters == null) return null;
        return `${(meters / 1000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} km`;
    };

    const MetricBadge = ({icon, value, bgColor, color}) => (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: '3px',
            padding: '2px 8px', borderRadius: '8px', fontSize: '15px',
            fontWeight: 600, backgroundColor: bgColor, color: color
        }}>
            {icon && <span>{icon}</span>}
            <span>{value}</span>
        </Box>
    );

    const MetricSkeleton = () => (
        <Skeleton variant="rounded" width={65} height={20} sx={{borderRadius: '8px'}}/>
    );

    return (
        <Box p={1} className={"laveuse-list-panel"} display="flex" flexDirection="column" gap={0.75}>
            <Box sx={{px: 1, py: 1, borderBottom: '1px solid #e2e8f0'}}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Rechercher (nom, opérateur, immatriculation)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{fontSize: 18, color: '#718096'}}/>
                                </InputAdornment>
                            ),
                            sx: {fontSize: '13px', borderRadius: '8px'}
                        }
                    }}
                />
            </Box>
            {sorted?.map(lav => {
                const engine = getEngineState(lav);
                const loaded = hasTelematicsLoaded(lav.id);
                const address = telematics?.[lav.id]?.live?.address ?? lav?.live?.address;
                const speed = getTelemetricValue(lav, 'speed');
                const odometer = getTelemetricValue(lav, 'odometer');
                const defLevel = getTelemetricValue(lav, 'defLevel');
                const fuelPercent = getTelemetricValue(lav, 'fuelPercent');
                const engineRpm = getTelemetricValue(lav, 'engineRpm');
                const faultCodes = telematics?.[lav.id]?.live?.faultCodes;
                const hasFaults = Array.isArray(faultCodes) && faultCodes.length > 0;
                const isSelected = lav.id === selectedId;
                const { backgroundColor: cardBg, borderColor: cardBorder, backgroundImage: cardBgImage } = getCardStyle(engine, speed, hasFaults);

                return (
                    <motion.div
                        key={lav.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0, backgroundColor: cardBg, boxShadow: isSelected ? '0 0 0 2px #185FA5' : '0 0 0 0px transparent' }}
                        transition={{ duration: 0.25, backgroundColor: { duration: 0.4, ease: "easeInOut" }, boxShadow: { duration: 0.15 } }}
                        onClick={() => onSelect?.(lav)}
                        style={{
                            borderRadius: '12px',
                            padding: '10px 12px',
                            cursor: 'pointer',
                            border: `1px solid ${isSelected ? '#185FA5' : cardBorder}`,
                            backgroundImage: cardBgImage,
                        }}
                    >
                        {/* Top row: name + operator + engine dot */}
                        <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 0.5}}>
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'}}>
                                    <Typography sx={{fontWeight: 700, fontSize: '18px', color: '#1e3a5f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                        {lav.description || lav.immatriculation}
                                        {lav.description && lav.immatriculation && (
                                            <Typography component="span" sx={{fontWeight: 400, fontSize: '14px', color: '#718096'}}>
                                                {` - ${lav.immatriculation}`}
                                            </Typography>
                                        )}
                                    </Typography>
                                    <AnimatePresence>
                                        {lav.client && (
                                            <motion.span
                                                key="client"
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.8 }}
                                                transition={{ duration: 0.18 }}
                                                style={{ display: 'inline-flex', alignItems: 'center',
                                                    backgroundColor: '#ede9fe', borderRadius: '20px', padding: '2px 9px',
                                                    fontSize: '11px', fontWeight: 600, color: '#5b21b6', whiteSpace: 'nowrap' }}
                                            >
                                                🏢 {lav.client}
                                            </motion.span>
                                        )}
                                        {lav.tourneeLibelle && (
                                            <motion.span
                                                key="tournee"
                                                initial={{ opacity: 0, scale: 0.8 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.8 }}
                                                transition={{ duration: 0.18 }}
                                                style={{ display: 'inline-flex', alignItems: 'center',
                                                    backgroundColor: '#fef3c7', borderRadius: '20px', padding: '2px 9px',
                                                    fontSize: '11px', fontWeight: 600, color: '#92400e', whiteSpace: 'nowrap' }}
                                            >
                                                🗺️ {lav.tourneeLibelle}
                                            </motion.span>
                                        )}
                                    </AnimatePresence>
                                </Box>
                                <Typography sx={{fontSize: '14px', color: '#718096'}}>
                                    {lav.operateur?.libelle
                                        ? `👤 ${lav.operateur.libelle}`
                                        : <NoUserIcon fontSize={14} color={"grey"}/>
                                    }
                                </Typography>
                            </Box>
                            {/* Engine state badge pill */}
                            {loaded ? (
                                <Box sx={{flexShrink: 0}}>
                                    <AnimatePresence mode="wait">
                                    {engine === 'lavage' && (
                                        <motion.span key="lavage"
                                            initial={{ opacity: 0, scale: 0.85 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.85 }}
                                            transition={{ duration: 0.18 }}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                backgroundColor: '#213067', borderRadius: '20px', padding: '5px 12px',
                                                fontSize: '12px', fontWeight: 500, color: '#FFF' }}
                                        >
                                            <Box sx={{
                                                width: 7, height: 7, borderRadius: '50%', backgroundColor: '#FFF',
                                                '@keyframes statusPulse': {
                                                    '0%,100%': {opacity: 1, transform: 'scale(1)'},
                                                    '50%': {opacity: 0.5, transform: 'scale(0.7)'}
                                                },
                                                animation: 'statusPulse 2s infinite'
                                            }}/>
                                            En lavage
                                        </motion.span>
                                    )}
                                    {engine === 'on' && speed > 0 && (
                                        <motion.span key="on"
                                            initial={{ opacity: 0, scale: 0.85 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.85 }}
                                            transition={{ duration: 0.18 }}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                backgroundColor: '#d5eabd', borderRadius: '20px', padding: '5px 12px',
                                                fontSize: '12px', fontWeight: 500, color: '#27500A' }}
                                        >
                                            <Box sx={{
                                                width: 7, height: 7, borderRadius: '50%', backgroundColor: '#3B6D11',
                                                '@keyframes statusPulse': {
                                                    '0%,100%': {opacity: 1, transform: 'scale(1)'},
                                                    '50%': {opacity: 0.5, transform: 'scale(0.7)'}
                                                },
                                                animation: 'statusPulse 2s infinite'
                                            }}/>
                                            En tournée
                                        </motion.span>
                                    )}
                                    {(engine === 'idle' || (engine === 'on' && !(speed > 0))) && (
                                        <motion.span key="idle"
                                            initial={{ opacity: 0, scale: 0.85 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.85 }}
                                            transition={{ duration: 0.18 }}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                backgroundColor: '#e2e8f0', borderRadius: '20px', padding: '5px 12px',
                                                fontSize: '12px', fontWeight: 500, color: '#4a5568' }}
                                        >
                                            <Box sx={{width: 7, height: 7, borderRadius: '50%', backgroundColor: '#718096'}}/>
                                            En pause
                                        </motion.span>
                                    )}
                                    </AnimatePresence>
                                </Box>
                            ) : (
                                <Skeleton variant="rounded" width={80} height={28} sx={{borderRadius: '20px'}}/>
                            )}
                        </Stack>

                        {/* Address */}
                        {loaded ? (
                            <Typography sx={{fontSize: '14px', color: '#718096', mb: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                📍 {address || "—"}
                            </Typography>
                        ) : (
                            <Skeleton variant="text" width="80%" sx={{fontSize: '11px', mb: 0.75}}/>
                        )}

                        {/* Fault codes banner */}
                        {hasFaults && (
                            <Box sx={{
                                backgroundColor: '#fff5f5', border: '1px solid #fca5a5',
                                borderRadius: '8px', padding: '5px 8px', mb: 0.75,
                                fontSize: '11px', color: '#e74c3c', fontWeight: 600,
                                display: 'flex', alignItems: 'flex-start', gap: '5px'
                            }}>
                                ⚠️ {faultCodes.length === 1 ? faultCodes[0] : `${faultCodes.length} défaut(s) : ${faultCodes[0]}`}
                            </Box>
                        )}

                        {/* Metric badges */}
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {loaded ? (
                                <>
                                    {defLevel != null && (
                                        <MetricBadge
                                            icon="💧"
                                            value={`${defLevel.toFixed(0)}%`}
                                            bgColor="#e6fffa"
                                            color={defLevel < 20 ? '#e74c3c' : defLevel < 40 ? '#f39c12' : '#2b7a78'}
                                        />
                                    )}
                                    {fuelPercent != null ? (
                                        <MetricBadge
                                            icon="⛽"
                                            value={`${fuelPercent}%`}
                                            bgColor="#fefce8"
                                            color={fuelPercent < 20 ? '#e74c3c' : fuelPercent < 40 ? '#f39c12' : '#276749'}
                                        />
                                    ) : engineRpm != null ? (
                                        <MetricBadge
                                            icon="🔧"
                                            value={`${engineRpm} rpm`}
                                            bgColor="#f0f4ff"
                                            color="#2b6cb0"
                                        />
                                    ) : null}
                                    {odometer != null && (
                                        <MetricBadge
                                            value={formatOdometer(odometer)}
                                            bgColor="#f0fff4"
                                            color="#276749"
                                        />
                                    )}
                                    {speed != null && (
                                        <MetricBadge
                                            value={`${speed} km/h`}
                                            bgColor="#ebf8ff"
                                            color="#2b6cb0"
                                        />
                                    )}
                                </>
                            ) : (
                                <>
                                    <MetricSkeleton/>
                                    <MetricSkeleton/>
                                    <MetricSkeleton/>
                                </>
                            )}
                        </Stack>
                    </motion.div>
                );
            })}
        </Box>
    );
}
